import React, { useEffect } from "react";
import { useDrag, useDragLayer } from "react-dnd";
import { playPickupSound } from "./sounds";
import "./ShapeTray.css";

const SHAPE_TYPE = "SHAPE";

function ShapePreview({ shape, idx, isDragLayer }) {
  const [{ isDragging }, drag, preview] = useDrag({
    type: SHAPE_TYPE,
    item: () => {
      playPickupSound();
      return { shape, idx };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    const emptyImage = new Image();
    emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    if (preview) {
      preview(emptyImage, { captureDraggingState: true });
    }
  }, [preview]);

  // If this is rendered inside the custom drag layer, don't attach the 'drag' ref
  const ref = isDragLayer ? null : drag;

  // Hide the original element in the tray while dragging, or keep it semi-transparent
  const opacity = isDragging && !isDragLayer ? 0.3 : 1;
  const filter = isDragLayer ? "drop-shadow(0 20px 15px rgba(0,0,0,0.5))" : "none";
  return (
    <div
      className={`shape-preview${isDragLayer ? " drag-layer-preview" : ""}`}
      ref={ref}
      style={{ opacity, filter }}
    >
      {shape.map((row, i) => (
        <div className="shape-row" key={i}>
          {row.map((cell, j) => {
            const isBomb = cell === 2;
            return (
              <div
                className={`shape-cell${cell ? " filled" : ""}${isBomb ? " bomb" : ""}${isDragging && cell ? " dragging" : ""}`}
                key={j}
              >
                {isBomb && <div className="bomb-core"></div>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ShapeTray({ shapes, canGenerate }) {
  return (
    <div className="shape-tray">
      {shapes.length === 0 && canGenerate && <div>Generating new shapes...</div>}
      {shapes.map((shape, idx) => (
        <ShapePreview shape={shape} idx={idx} key={idx} />
      ))}
    </div>
  );
}

export function CustomDragLayer() {
  const { isDragging, item, currentOffset, initialClientOffset, initialSourceClientOffset, clientOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    initialClientOffset: monitor.getInitialClientOffset(),
    initialSourceClientOffset: monitor.getInitialSourceClientOffset(),
    clientOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging || !clientOffset || !initialClientOffset || !initialSourceClientOffset) {
    return null;
  }

  // 1. Dinamik Ölçeklendirme (Dynamic Scaling)
  const boardEl = document.querySelector('.board');
  const boardCellSize = boardEl ? (boardEl.getBoundingClientRect().width / 8) : 40;

  // Tepsideki hücrenin CSS boyutu: width(24) + margin(1+1) = 26px
  const trayCellSize = 26;
  const scaleRatio = boardCellSize / trayCellSize;

  // 2. Pivot & Offset Yönetimi
  // Bloğun ilk tutulduğu an ile sol-üst köşesi arasındaki fiziksel fark
  const grabOffsetX = initialClientOffset.x - initialSourceClientOffset.x;
  const grabOffsetY = initialClientOffset.y - initialSourceClientOffset.y;

  // Blok büyüdüğünde bu farkın da aynı oranda büyümesi lazım ki imleçten kaçmasın
  const scaledGrabOffsetX = grabOffsetX * scaleRatio;
  const scaledGrabOffsetY = grabOffsetY * scaleRatio;

  // Farenin anlık konumundan, büyütülmüş farkı çıkarıyoruz (Screen Space to World/Scaled Space)
  const x = clientOffset.x - scaledGrabOffsetX;
  const y = clientOffset.y - scaledGrabOffsetY;

  return (
    <div style={{
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: 9999,
      left: 0,
      top: 0,
      transform: `translate(${x}px, ${y}px) scale(${scaleRatio})`,
      transformOrigin: 'top left'
    }}>
      <ShapePreview shape={item.shape} idx={item.idx} isDragLayer={true} />
    </div>
  );
}

export { SHAPE_TYPE };
export default ShapeTray;
